'use client';

import { Logo } from '@eidas-pro/assets';
import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/navigation';
import { useState, useEffect } from 'react';
import { LanguageSwitcher, MobileLanguageSwitcher } from './LanguageSwitcher';

export function BlogNavbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const t = useTranslations('common');

    useEffect(() => {
        const handleScroll = () => {
            // eslint-disable-next-line no-undef
            setIsScrolled(window.scrollY > 20);
        };

        // eslint-disable-next-line no-undef
        window.addEventListener('scroll', handleScroll);
        // eslint-disable-next-line no-undef
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { href: '/' as const, label: t('header.nav.home') },
        { href: '/blog' as const, label: t('header.nav.blog') },
        { href: '/docs' as const, label: t('header.nav.docs') },
        { href: '/#demo' as const, label: t('header.nav.demo') },
        { href: '/support' as const, label: t('header.nav.support') },
    ];

    return (
        <nav
            className={`
                fixed top-0 left-0 right-0 z-50
                transition-all duration-300 ease-out
                ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200'
                        : 'bg-white border-b border-gray-100'
                }
            `}>
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-3 group">
                        <Logo
                            width={48}
                            height={48}
                            className="transition-transform group-hover:scale-105"
                            aria-label="eIDAS Pro"
                        />
                        <span className="font-serif text-2xl text-trust-navy">eIDAS Pro</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className="
                                    text-sm font-medium text-gray-700 hover:text-gray-900
                                    relative py-2
                                    after:absolute after:bottom-0 after:left-0 after:right-0
                                    after:h-[2px] after:bg-gray-900
                                    after:scale-x-0 hover:after:scale-x-100
                                    after:transition-transform after:duration-300
                                    after:origin-left
                                ">
                                {link.label}
                            </Link>
                        ))}
                        <LanguageSwitcher />
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden p-2 text-gray-700 hover:text-gray-900 transition-colors"
                        aria-label="Toggle menu">
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div
                className={`
                    md:hidden
                    overflow-hidden transition-all duration-300 ease-out
                    ${isMobileMenuOpen ? 'max-h-[500px] border-t border-gray-200' : 'max-h-0'}
                `}>
                <div className="container mx-auto px-4 py-4 bg-white">
                    <div className="flex flex-col gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="
                                    text-base font-medium text-gray-700 hover:text-gray-900
                                    py-3 px-4 rounded-lg
                                    hover:bg-gray-50
                                    transition-colors
                                ">
                                {link.label}
                            </Link>
                        ))}
                        <MobileLanguageSwitcher />
                    </div>
                </div>
            </div>
        </nav>
    );
}
